import React, { useState, useEffect } from "react"
import package1 from "../assets/package1.webp"
import package2 from "../assets/package2.webp"
import package3 from "../assets/package3.webp"
import { Link } from "react-router-dom"

export const Offer = () => {
  const [starterPrice, setStarterPrice] = useState(0)
  const [familyPrice, setFamilyPrice] = useState(0)
  const [luxuryPrice, setLuxuryPrice] = useState(0)

  // Price animation effect
  useEffect(() => {
    const animatePrice = (
      setPrice: (price: number) => void,
      targetPrice: number
    ) => {
      let currentPrice = 0
      const increment = targetPrice / 100 // Adjust speed of animation
      const interval = setInterval(() => {
        currentPrice += increment
        if (currentPrice >= targetPrice) {
          setPrice(targetPrice)
          clearInterval(interval)
        } else {
          setPrice(Math.round(currentPrice))
        }
      }, 20) // Adjust interval for smoother/faster animation
    }

    animatePrice(setStarterPrice, 150000)
    animatePrice(setFamilyPrice, 250000)
    animatePrice(setLuxuryPrice, 400000)
  }, [])

  return (
    <section className="bg-gray-50 py-24">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="text-3xl md:text-4xl font-semibold text-gray-800 text-center mb-8 animate-fade-in">
          Explore Our Customizable Modular Home Packages
        </h2>

        {/* Introduction */}
        <div className="mb-12 animate-fade-in">
          <p className="text-gray-700 leading-relaxed text-base md:text-lg text-center">
            Discover the perfect modular home package to fit your needs and
            budget. Our customizable options allow you to create a home that's
            uniquely yours.
          </p>
        </div>

        {/* Package 1: The "Starter" Package */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 md:mb-12 overflow-hidden animate-slide-in-left">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Image */}
              <div className="w-full md:w-1/3">
                <img
                  src={package2}
                  alt="Starter Package: A cozy modular home with a modern design."
                  className="w-full h-auto rounded-lg object-cover"
                  loading="lazy"
                />
              </div>
              {/* Content */}
              <div className="w-full md:w-2/3">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-4">
                  The "Starter" Package
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  A perfect entry point into modular living, offering essential
                  features and a streamlined design.
                </p>

                {/* Features and Customization Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      Key Features:
                    </h4>
                    <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                      <li>Two Bedrooms, One Bathroom</li>
                      <li>Open-Concept Living Area</li>
                      <li>Energy-Efficient Windows and Doors</li>
                      <li>Basic Kitchen Appliances</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      Customization Options:
                    </h4>
                    <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                      <li>Choice of Exterior Siding Color</li>
                      <li>Upgrade to Stainless Steel Appliances</li>
                      <li>Add a Deck or Patio</li>
                    </ul>
                  </div>
                </div>

                {/* Pricing and Link Button */}
                <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
                  <div>
                    <p className="text-gray-600">Starting at:</p>
                    <p className="text-3xl md:text-4xl font-bold text-green-600">
                      ${starterPrice.toLocaleString()}
                    </p>
                  </div>
                  <Link
                    to="/contact"
                    className="inline-block bg-green-800 text-white py-2 px-6 md:py-3 md:px-8 rounded-full hover:bg-green-600 font-semibold transition duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 text-sm md:text-base"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Package 2: The "Family" Package */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 md:mb-12 overflow-hidden animate-slide-in-right">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Image */}
              <div className="w-full md:w-1/3">
                <img
                  src={package1}
                  alt="Family Package: A spacious modular home with a modern kitchen and garage."
                  className="w-full h-auto rounded-lg object-cover"
                  loading="lazy"
                />
              </div>
              {/* Content */}
              <div className="w-full md:w-2/3">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-4">
                  The "Family" Package
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Designed for growing families, offering spacious living areas
                  and multiple bedrooms.
                </p>

                {/* Features and Customization Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      Key Features:
                    </h4>
                    <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                      <li>Three Bedrooms, Two Bathrooms</li>
                      <li>Large Living Room and Dining Area</li>
                      <li>Gourmet Kitchen with Island</li>
                      <li>Two-Car Garage</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      Customization Options:
                    </h4>
                    <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                      <li>Choice of Flooring and Countertop Materials</li>
                      <li>Add a Fireplace</li>
                      <li>Upgrade to Smart Home Technology</li>
                    </ul>
                  </div>
                </div>

                {/* Pricing and Link Button */}
                <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
                  <div>
                    <p className="text-gray-600">Starting at:</p>
                    <p className="text-3xl md:text-4xl font-bold text-green-600">
                      ${familyPrice.toLocaleString()}
                    </p>
                  </div>
                  <Link
                    to="/contact"
                    className="inline-block bg-green-800 text-white py-2 px-6 md:py-3 md:px-8 rounded-full hover:bg-green-600 font-semibold transition duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 text-sm md:text-base"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Package 3: The "Luxury" Package */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 md:mb-12 overflow-hidden animate-slide-in-left">
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Image */}
              <div className="w-full md:w-1/3">
                <img
                  src={package3}
                  alt="Luxury Package: A high-end modular home with premium finishes and outdoor living space."
                  className="w-full h-auto rounded-lg object-cover"
                  loading="lazy"
                />
              </div>
              {/* Content */}
              <div className="w-full md:w-2/3">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-4">
                  The "Luxury" Package
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Experience the ultimate in modular living with premium
                  features, high-end finishes, and unparalleled comfort.
                </p>

                {/* Features and Customization Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      Key Features:
                    </h4>
                    <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                      <li>Four Bedrooms, Three Bathrooms</li>
                      <li>Spacious Living Areas with High Ceilings</li>
                      <li>Gourmet Kitchen with Premium Appliances</li>
                      <li>Smart Home Automation System</li>
                      <li>Outdoor Living Space with Fireplace</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
                      Customization Options:
                    </h4>
                    <ul className="list-disc list-inside text-gray-700 leading-relaxed">
                      <li>
                        Choice of Premium Flooring and Countertop Materials
                      </li>
                      <li>Add a Swimming Pool or Hot Tub</li>
                      <li>Customize the Landscaping</li>
                    </ul>
                  </div>
                </div>

                {/* Pricing and Link Button */}
                <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
                  <div>
                    <p className="text-gray-600">Starting at:</p>
                    <p className="text-3xl md:text-4xl font-bold text-green-600">
                      ${luxuryPrice.toLocaleString()}
                    </p>
                  </div>
                  <Link
                    to="/contact"
                    className="inline-block bg-green-800 text-white py-2 px-6 md:py-3 md:px-8 rounded-full hover:bg-green-600 font-semibold transition duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 text-sm md:text-base"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
